import React from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, MagnifyingGlassIcon, MapPinIcon } from '@heroicons/react/24/outline';

/**
 * Unified mobile search overlay component
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether overlay is open
 * @param {Function} props.onClose - Close handler
 * @param {string} props.title - Modal title (e.g., "Search Starters", "Search Projects")
 * @param {string} props.searchPlaceholder - Input placeholder
 * @param {string} props.mobileSearchTerm - Current search term value
 * @param {Function} props.onSearchTermChange - Search term change handler
 * @param {Function} props.onSearch - Search submit handler
 * @param {Array} props.selectedTags - Currently selected tag IDs
 * @param {Function} props.onTagToggle - Tag toggle handler: (tagId) => void
 * @param {Array} props.allTags - All available tags [{ id, label, icon, description }]
 * @param {string} props.sortBy - Current sort value
 * @param {Array} props.sortOptions - Sort options [{ value, label }]
 * @param {Function} props.onSortChange - Sort change handler: (value) => void
 * @param {boolean} props.showSort - Show sort options (default: true)
 * @param {boolean} props.showTags - Show tag filtering (default: true)
 * @param {boolean} props.mapAreaFilterActive - Whether map area filter is active
 * @param {Function} props.onClearMapAreaFilter - Clear map area filter handler
 * @param {Function} props.onClearAll - Clear all filters handler
 * @param {boolean} props.enableArchetype - Show archetype filter (default: false)
 * @param {string} props.selectedArchetype - Current archetype value
 * @param {Function} props.onArchetypeChange - Archetype change handler
 * @param {Array} props.archetypeOptions - Archetype options [{ value, label }]
 * @param {Function} props.onNearbyToggle - Nearby tag toggle handler (special handling)
 */
const MobileSearchOverlay = ({
  isOpen,
  onClose,
  title = 'Search',
  searchPlaceholder = 'Search',
  mobileSearchTerm = '',
  onSearchTermChange,
  onSearch,
  selectedTags = [],
  onTagToggle,
  allTags = [],
  sortBy,
  sortOptions = [],
  onSortChange,
  showSort = true,
  showTags = true,
  mapAreaFilterActive = false,
  onClearMapAreaFilter,
  onClearAll,
  enableArchetype = false,
  selectedArchetype,
  onArchetypeChange,
  archetypeOptions = [],
  onNearbyToggle,
}) => {
  if (!isOpen || typeof document === 'undefined') return null;

  return createPortal(
    <div 
      className="fixed inset-0 z-[2147483647] bg-white dark:bg-gray-900" 
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-4 mb-4">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{title}</h2>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 flex items-center justify-center shadow-sm border border-gray-200 dark:border-gray-700 shadow-lg"
            aria-label="Close search"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Search Input and Filters */}
        <div className="flex-shrink-0 px-4 space-y-4">
          {/* Search Input */}
          <div className="relative w-full">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={mobileSearchTerm}
              onChange={(e) => onSearchTermChange(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  onSearch();
                }
              }}
              className="block w-full pl-10 pr-10 py-4 border border-gray-300 dark:border-gray-600 rounded-xl leading-5 bg-white text-sm dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
            />
            {mobileSearchTerm && (
              <button
                type="button"
                onClick={() => onSearchTermChange('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                aria-label="Clear search"
              >
                <XMarkIcon className="h-5 h-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
              </button>
            )}
          </div>

          {/* Map Area Filter */}
          {mapAreaFilterActive && onClearMapAreaFilter && (
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-full text-sm font-medium">
                <MapPinIcon className="w-4 h-4" />
                Map Area
              </span>
              <button
                onClick={onClearMapAreaFilter}
                className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 underline"
                aria-label="Clear map area filter"
              >
                Clear
              </button>
            </div>
          )}

          {/* Sort Options */}
          {showSort && sortOptions.length > 0 && onSortChange && (
            <div>
              <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2 block">Sort by</span>
              <div className="inline-flex items-center bg-gray-100 dark:bg-gray-800 rounded-full p-1.5 w-full">
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => onSortChange(option.value)}
                    aria-label={`Sort by ${option.label}`}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium w-[50%] transition-all ${
                      sortBy === option.value
                        ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Archetype Filter */}
          {enableArchetype && archetypeOptions.length > 0 && onArchetypeChange && (
            <div>
              <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2 block">Type</span>
              <div className="inline-flex items-center bg-gray-100 dark:bg-gray-800 rounded-full p-1.5 w-full">
                {archetypeOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => onArchetypeChange(option.value)}
                    aria-label={`Filter by ${option.label}`}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium flex-1 transition-all ${
                      selectedArchetype === option.value
                        ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Tags List */}
        {showTags && allTags.length > 0 && (
          <div className="flex-1 min-h-0 px-4 pt-4 relative">
            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">Filter by</p>
            <div 
              className="absolute left-4 right-4 bottom-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden"
              style={{ top: 'calc(1rem + 1.5rem + 0.75rem)' }}
            >
              <div 
                className="overflow-y-auto mobile-scroll"
                style={{ 
                  height: '100%',
                  WebkitOverflowScrolling: 'touch',
                  touchAction: 'pan-y'
                }}
              >
                {allTags.map((tag) => {
                  const isSelected = selectedTags.includes(tag.id);
                  const isNearbyTag = tag.id === 'nearby';
                  return (
                    <button
                      key={tag.id}
                      onClick={() => {
                        if (isNearbyTag && onNearbyToggle) {
                          onNearbyToggle();
                        } else if (onTagToggle) {
                          onTagToggle(tag.id);
                        }
                      }}
                      className={`
                        w-full flex items-center gap-4 px-4 py-3.5 text-left
                        hover:bg-gray-50 dark:hover:bg-gray-700
                        transition-colors duration-150
                        ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''}
                      `}
                    >
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                        <span className="text-xl">{tag.icon}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`font-semibold text-sm ${isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-white'}`}>
                          {tag.label}
                        </div>
                        {tag.description && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {tag.description}
                          </div>
                        )}
                      </div>
                      {isSelected && (
                        <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full"></div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Footer Actions */}
        <div
          className="px-6 py-4 mb-2 border-t border-gray-200 dark:border-gray-800 bg-white/95 dark:bg-gray-900/95"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.5rem)' }}
        >
          <div className="flex items-center justify-between gap-3">
            {onClearAll && (
              <button
                onClick={onClearAll}
                className="text-sm font-semibold text-gray-700 dark:text-gray-200 underline"
              >
                Clear all
              </button>
            )}
            {onSearch && (
              <button
                onClick={onSearch}
                className="inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-white font-semibold text-base bg-gradient-to-r from-pink-500 via-red-500 to-orange-500 shadow-lg min-w-[120px]"
              >
                <MagnifyingGlassIcon className="w-5 h-5" />
                Search
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MobileSearchOverlay;

